import { ZIP_BUNDLE_EVENTS } from 'Sp/Angular/Events/Client/Gallery';

export default [
    '$rootScope',
    '$scope',
    '$interval',
    '$routeParams',
    '$window',
    'spAppData',
    'spClientCredentials',
    'SPClientData',
    function BundleController(
        $rootScope,
        $scope,
        $interval,
        $routeParams,
        $window,
        spAppData,
        spClientCredentials,
        SPClientData
    ) {
        const PAGE_STATE = {
            AUTHORIZE: 'authorize',
            BROWSER_GOOGLE_SEARCH_APP: 'browser-google-search-app',
            PENDING: 'pending',
            READY: 'ready',
            TIMEOUT: 'timeout'
        };
        const ZIP_BUNDLE_TITLE_KEY = {
            DEFAULT: 'zipBundleTitle',
            MEDIA_ITEMS: 'zipBundleTitleMediaItems',
            SLIDESHOW: 'zipBundleTitleSlideshow',
            SLIDESHOWS: 'zipBundleTitleSlideshows',
            VIDEO: 'zipBundleTitleVideo',
            VIDEOS: 'zipBundleTitleVideos'
        };
        // If the ZIP Bundle has no email set, there's no reason for us to
        // present the form for authentication.  Instead, just make a
        // separate request to fetch the ZIP Bundle data.
        const clientCredentialsEmail = spClientCredentials.get().readableEmail;
        const clientCredentialsZipBundleKey = spClientCredentials.get().zipBundleKey;
        let pollingTimer = null;

        $scope.PAGE_STATE = PAGE_STATE;
        $scope.pageState = PAGE_STATE.AUTHORIZE;
        $scope.zipBundleAppData = spAppData.get('zipBundleData');
        $scope.zipBundleData = null;
        $scope.showHelp = false;
        $scope.credentials = { email: clientCredentialsEmail };
        $scope.errors = {};

        const isGoogleSearchApp = !!$window.navigator.userAgent.match(/(?:GSA)\/(\d+(\.?_?\d+)+)/i);

        if (isGoogleSearchApp) {
            $scope.pageState = PAGE_STATE.BROWSER_GOOGLE_SEARCH_APP;
            return;
        }

        if (!$scope.zipBundleAppData.settings.hasEmail || clientCredentialsEmail) {
            authenticateBundle();
        }

        $scope.authenticateBundle = authenticateBundle;

        // Picks a title that reflects what's actually in the bundle: the
        // "photos are ready" default only when photos are the only thing
        // present, a singular/plural title for a single media type (video or
        // slideshow) on its own, or a generic "media items" title once more
        // than one type is mixed together.
        $scope.getZipBundleTitleKey = function getZipBundleTitleKey() {
            const zipBundleData = $scope.zipBundleData || {};
            const fileCount = (zipBundleData.files || []).length;
            const videoCount = (zipBundleData.videos || []).length;
            const slideshowCount = (zipBundleData.slideshows || []).length;

            const presentTypeCount = [fileCount, videoCount, slideshowCount].filter(
                (count) => count > 0
            ).length;

            if (presentTypeCount > 1) {
                return ZIP_BUNDLE_TITLE_KEY.MEDIA_ITEMS;
            }

            if (slideshowCount > 0) {
                return slideshowCount === 1
                    ? ZIP_BUNDLE_TITLE_KEY.SLIDESHOW
                    : ZIP_BUNDLE_TITLE_KEY.SLIDESHOWS;
            }

            if (videoCount > 0) {
                return videoCount === 1 ? ZIP_BUNDLE_TITLE_KEY.VIDEO : ZIP_BUNDLE_TITLE_KEY.VIDEOS;
            }

            // Either photos only, or nothing loaded yet - both fall back to
            // the "photos are ready" default.
            return ZIP_BUNDLE_TITLE_KEY.DEFAULT;
        };

        $scope.downloadFile = function downloadFile(zipBundleFile) {
            SPClientData.logBundleDownloaded(
                $routeParams.zipBundleId,
                function doNothing() {
                    // Success
                },
                function doNothing() {
                    // Error
                }
            );

            $window.location.href = zipBundleFile.url;
        };

        function authenticateBundle() {
            SPClientData.bundleAuth(
                $routeParams.zipBundleId,
                $scope.credentials.email,
                $scope.credentials.doesAcceptTerms,
                function withSuccessResponse({ zipBundleData }) {
                    $scope.zipBundleData = zipBundleData;

                    if (zipBundleData.ready) {
                        $scope.pageState = PAGE_STATE.READY;

                        return;
                    }

                    pollForZipBundle();

                    $scope.pageState = PAGE_STATE.PENDING;
                },
                function withErrorResponse(response) {
                    if ($scope.pageState === PAGE_STATE.AUTHORIZE) {
                        $scope.errors = response.errors;
                    } else {
                        if (response.errors.email && response.errors.email.notSame) {
                            $scope.errors = null;
                            $scope.credentials.email = null;
                            $scope.pageState = PAGE_STATE.AUTHORIZE;
                        }
                    }
                }
            );
        }

        function pollForZipBundle() {
            // Already polling (e.g. authenticateBundle() re-reported "not ready" while a
            // poll was already in flight) - don't start a second overlapping timer/listener.
            if (pollingTimer) {
                return;
            }

            const POLLING_INTERVAL = 5000;
            // Show the "taking longer than expected" message after ~60s of polling, but
            // keep polling quietly in the background well past that: a bundle containing
            // a slideshow has to wait on an actual video render (an external, asynchronous
            // encode) before it can finish, which routinely takes longer than a plain
            // photo/video zip. If it finishes while this tab stays open, the page should
            // flip to ready on its own instead of requiring a manual refresh.
            const ATTEMPTS_BEFORE_TIMEOUT_MESSAGE = 60000 / POLLING_INTERVAL;
            // Absolute safety net so a bundle that's genuinely stuck (never reaches
            // Downloadable or Unrecoverable Error) doesn't poll forever.
            const MAX_ATTEMPTS = (15 * 60 * 1000) / POLLING_INTERVAL;
            let attempts = 0;

            const offZipBundleGot = $rootScope.$on(ZIP_BUNDLE_EVENTS.GOT, (_, response) => {
                if (response.zipBundleStatusId === 3) {
                    stopPollingForZipBundle();
                    authenticateBundle();
                } else if (response.zipBundleStatusId === 7) {
                    // Unrecoverable Error - this bundle will never become ready, so give up
                    // rather than continuing to check forever in the background.
                    giveUpPolling();
                }
            });

            function stopPollingForZipBundle() {
                $interval.cancel(pollingTimer);
                pollingTimer = null;
                offZipBundleGot();
            }

            // Stop polling and show the same "taking longer than expected" messaging used
            // once the timeout threshold passes - there is no separate error page state, so
            // any terminal non-ready outcome (missing key, unrecoverable error, exceeding the
            // absolute safety net) surfaces the same way a plain timeout would.
            function giveUpPolling() {
                stopPollingForZipBundle();
                $scope.pageState = PAGE_STATE.TIMEOUT;
            }

            pollingTimer = $interval(() => {
                const zipBundleKey =
                    clientCredentialsZipBundleKey || $scope.zipBundleData.bundleKey;

                if (!zipBundleKey) {
                    giveUpPolling();
                    return;
                }

                attempts += 1;

                if (attempts === ATTEMPTS_BEFORE_TIMEOUT_MESSAGE) {
                    $scope.pageState = PAGE_STATE.TIMEOUT;
                }

                if (attempts >= MAX_ATTEMPTS) {
                    stopPollingForZipBundle();
                    return;
                }

                $rootScope.$emit(ZIP_BUNDLE_EVENTS.GET, {
                    zipBundleId: $routeParams.zipBundleId,
                    zipBundleKey
                });
            }, POLLING_INTERVAL);

            $scope.$on('$destroy', stopPollingForZipBundle);
        }
    }
];
